import { useState } from "react"
import { MoreHorizontal, Pencil, FolderInput, Trash2, Star, StarOff } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { FolderPicker } from "@/components/capture/FolderPicker"

interface FileContextMenuProps {
  path: string
  name: string
  rootFolder: string
  onRename: (path: string, newName: string) => void
  onDelete: (path: string) => void
  onMove: (fromPath: string, toPath: string) => void
  isFavorite?: boolean
  onToggleFavorite?: (path: string, name: string) => void
}

export function FileContextMenu({ path, name, rootFolder, onRename, onDelete, onMove, isFavorite, onToggleFavorite }: FileContextMenuProps) {
  const [renaming, setRenaming] = useState(false)
  const [newName, setNewName] = useState(name)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showFolderPicker, setShowFolderPicker] = useState(false)

  function handleRename() {
    if (newName && newName !== name) {
      onRename(path, newName)
    }
    setRenaming(false)
  }

  function handleDelete() {
    onDelete(path)
    setShowDeleteConfirm(false)
  }

  function handleMove(destination: string) {
    const fileName = path.split("/").pop()
    onMove(path, `${destination}/${fileName}`)
    setShowFolderPicker(false)
  }

  if (renaming) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleRename()
            if (e.key === "Escape") setRenaming(false)
          }}
          onBlur={handleRename}
          className="h-7 w-48 text-sm"
          autoFocus
          data-testid="rename-input"
        />
      </div>
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="rounded-md p-1 hover:bg-accent"
            data-testid={`context-menu-${name}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onToggleFavorite && (
            <DropdownMenuItem onClick={() => onToggleFavorite(path, name)} className="gap-2">
              {isFavorite ? <StarOff className="h-4 w-4" /> : <Star className="h-4 w-4" />}
              {isFavorite ? "Remove from Favorites" : "Add to Favorites"}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => { setNewName(name); setRenaming(true) }} className="gap-2">
            <Pencil className="h-4 w-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowFolderPicker(true)} className="gap-2">
            <FolderInput className="h-4 w-4" />
            Move
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setShowDeleteConfirm(true)}
            className="gap-2 text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete file</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{name}&quot;? This will remove it from Dropbox.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move folder picker */}
      <FolderPicker
        open={showFolderPicker}
        onClose={() => setShowFolderPicker(false)}
        onSelect={handleMove}
        rootPath={rootFolder}
      />
    </>
  )
}
